import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { resendVerification } from "@/lib/auth/authClient";
import { RouteTopBar } from "@/components/layout/RouteTopBar";
import { useLanguage } from "@/hooks/useLanguage";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "@tanstack/react-router";
import { trackEvent } from "@/lib/audit";

type Search = { email?: string };

export const Route = createFileRoute("/verify-email")({
  validateSearch: (search: Record<string, unknown>): Search => ({
    email: typeof search.email === "string" ? search.email : undefined,
  }),
  head: () => ({ meta: [{ title: "Verifica tu email · VerseSport" }] }),
  component: VerifyEmailPage,
});

function VerifyEmailPage() {
  const { email } = Route.useSearch();
  const { t } = useLanguage();
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  // P0.3 — poll auth session every 3s and listen to auth state changes.
  // As soon as the user confirms their email in another tab, we move them
  // straight into the activation funnel (NationGate fires on /nation-wars).
  useEffect(() => {
    let cancelled = false;
    const expected = (email ?? (() => {
      try { return window.localStorage.getItem("vs:pending_signup_email") ?? undefined; } catch { return undefined; }
    })())?.trim().toLowerCase();
    const go = () => {
      if (cancelled) return;
      cancelled = true;
      try { window.localStorage.removeItem("vs:pending_signup_email"); } catch { /* noop */ }
      void navigate({ to: "/nation-wars" });
    };
    const mismatchAndOut = async (actual: string | undefined) => {
      void trackEvent("signup_identity_mismatch", { expected, actual, surface: "verify_email" });
      try { await supabase.auth.signOut(); } catch { /* noop */ }
      toast.error("La sesión activa pertenece a otra cuenta. Inicia sesión con el email que acabas de registrar.");
      if (cancelled) return;
      cancelled = true;
      void navigate({ to: "/login", search: { from: undefined } });
    };
    const tick = async () => {
      const { data } = await supabase.auth.getSession();
      const session = data.session;
      if (!session) return;
      const actual = session.user?.email?.trim().toLowerCase();
      if (expected && actual && actual !== expected) {
        void mismatchAndOut(actual);
        return;
      }
      go();
    };
    void tick();
    const id = window.setInterval(() => { void tick(); }, 3000);
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if ((event === "SIGNED_IN" || event === "TOKEN_REFRESHED") && session) {
        const actual = session.user?.email?.trim().toLowerCase();
        if (expected && actual && actual !== expected) { void mismatchAndOut(actual); return; }
        go();
      }
    });
    return () => {
      cancelled = true;
      window.clearInterval(id);
      sub.subscription.unsubscribe();
    };
  }, [navigate, email]);

  const onResend = async () => {
    if (!email) { toast.error(t("verify_email_unavailable")); return; }
    setSubmitting(true);
    try {
      await resendVerification(email);
      toast.success(t("verify_resent_ok"));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("verify_resent_fail"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-background">
      <RouteTopBar fallback="/welcome" />
      <div className="mx-auto max-w-md px-4 py-12 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/15 text-primary">✉</div>
        <h1 className="mt-6 text-2xl font-black text-foreground">{t("verify_title")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {email ? t("verify_desc_with_email", { email }) : t("verify_desc_no_email")}
        </p>
        <button type="button" onClick={onResend} disabled={submitting || !email} className="mt-6 w-full rounded-xl bg-primary px-4 py-3 text-sm font-bold text-primary-foreground shadow disabled:opacity-60">
          {submitting ? "…" : t("verify_resend")}
        </button>
        <Link
          to="/"
          onClick={() => void trackEvent("verify_email_explore_as_guest", { surface: "verify_email" })}
          className="mt-3 block rounded-xl border border-primary/40 bg-primary/10 px-4 py-3 text-sm font-bold text-primary"
        >
          Explorar VerseSport ahora →
        </Link>
        <p className="mt-2 text-[11px] text-muted-foreground">
          🏆 Verifica tu correo para reclamar premios y recompensas.
        </p>
        <Link to="/login" search={{ from: undefined }} className="mt-4 block text-xs text-muted-foreground hover:text-foreground">{t("verify_goto_login")}</Link>
      </div>
    </main>
  );
}